import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface PipelineHygiene {
  pipeline_id: string;
  pipeline_name: string;
  company_name: string | null;
  product_type: string | null;
  location_id: string | null;
  location_name: string | null;
  open: number;
  won_last_90d: number;
  lost_last_90d: number;
  win_rate_90d: number | null;
  avg_days_open: number | null;
  at_first_stage: number;
  stale: number;
  missing_contact: number;
  unassigned: number;
  total_value: number;
  value_at_risk: number;
}
export interface StageRow { pipeline_id: string; pipeline_name: string; stage_name: string; stage_order: number | null; count: number; }
interface OppRow { pipeline_id: string; contact_email: string | null; contact_phone: string | null; last_stage_change_at: string | null; ghl_created_at: string | null; stage_name: string | null; stage_order: number | null; assigned_to: string | null; status: string | null; ghl_updated_at: string | null; monetary_value: number | null; }
export interface OrgLocation { id: string; name: string | null; slug: string | null; }

const STALE_DAYS = 14;

export function useGhlHygieneData(orgId: string) {
  const [rows, setRows] = useState<PipelineHygiene[]>([]);
  const [orgLocations, setOrgLocations] = useState<OrgLocation[]>([]);
  const [stageRows, setStageRows] = useState<StageRow[]>([]);
  const [hasConnections, setHasConnections] = useState<boolean | null>(null);
  const [lastSynced, setLastSynced] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!supabase) return;
    const db = supabase;
    setLoading(true); setError(null);
    try {
      const connRes = await db.from("ghl_connections").select("id, company_name, ghl_location_id, last_synced_at").eq("organization_id", orgId).eq("is_active", true);
      if (connRes.error) throw connRes.error;
      const conns = (connRes.data ?? []) as { id: string; company_name: string | null; ghl_location_id: string | null; last_synced_at: string | null }[];
      if (conns.length === 0) { setHasConnections(false); setLoading(false); return; }
      setHasConnections(true);
      const latestSync = conns.map((c) => c.last_synced_at).filter(Boolean).sort().at(-1) ?? null;
      setLastSynced(latestSync);
      const connIds = conns.map((c) => c.id);
      const connNameById = new Map<string, string | null>(conns.map((c) => [c.id, c.company_name ?? c.ghl_location_id ?? null]));

      const pipRes = await db.from("ghl_pipelines").select("id, ghl_connection_id, pipeline_name, product_type, location_id").in("ghl_connection_id", connIds).eq("is_active", true);
      if (pipRes.error) throw pipRes.error;
      const pipelines = (pipRes.data ?? []) as { id: string; ghl_connection_id: string; pipeline_name: string; product_type: string | null; location_id: string | null }[];
      if (pipelines.length === 0) { setRows([]); setStageRows([]); setLoading(false); return; }

      const pipelineIds = pipelines.map((p) => p.id);
      const locationUuids = [...new Set(pipelines.map((p) => p.location_id).filter((id): id is string => !!id))];
      const locationMap = new Map<string, string>();
      if (locationUuids.length > 0) {
        const locRes = await db.from("locations").select("id, name").in("id", locationUuids);
        if (!locRes.error) for (const loc of (locRes.data ?? []) as { id: string; name: string }[]) locationMap.set(loc.id, loc.name);
      }
      setOrgLocations(Array.from(locationMap.entries()).map(([id, name]) => ({ id, name, slug: null })));

      const oppRes = await db.from("ghl_opportunities").select("pipeline_id, contact_email, contact_phone, last_stage_change_at, ghl_created_at, stage_name, stage_order, assigned_to, status, ghl_updated_at, monetary_value").eq("organization_id", orgId).in("pipeline_id", pipelineIds);
      if (oppRes.error) throw oppRes.error;
      const opps = (oppRes.data ?? []) as OppRow[];
      const staleThreshold = new Date(); staleThreshold.setDate(staleThreshold.getDate() - STALE_DAYS);
      const winRateCutoff = new Date(); winRateCutoff.setDate(winRateCutoff.getDate() - 90);
      const nowMs = Date.now();
      const grouped = new Map<string, PipelineHygiene>(pipelines.map((p) => [p.id, { pipeline_id: p.id, pipeline_name: p.pipeline_name, company_name: connNameById.get(p.ghl_connection_id) ?? null, product_type: p.product_type ?? null, location_id: p.location_id ?? null, location_name: p.location_id ? (locationMap.get(p.location_id) ?? null) : null, open: 0, won_last_90d: 0, lost_last_90d: 0, win_rate_90d: null, avg_days_open: null, at_first_stage: 0, stale: 0, missing_contact: 0, unassigned: 0, total_value: 0, value_at_risk: 0 }]));
      const openAgeSums = new Map<string, { totalDays: number; count: number }>();
      for (const opp of opps) {
        const row = grouped.get(opp.pipeline_id); if (!row) continue;
        const status = String(opp.status ?? "").toLowerCase();
        if (status === "open") {
          row.open++; if ((opp.stage_order ?? 999) <= 1) row.at_first_stage++;
          const activityDate = new Date(opp.last_stage_change_at ?? opp.ghl_created_at ?? "1970-01-01"); if (activityDate < staleThreshold) row.stale++;
          const hasContact = (opp.contact_phone?.trim() ?? "") !== "" || (opp.contact_email?.trim() ?? "") !== "";
          if (!hasContact) { row.missing_contact++; row.value_at_risk += opp.monetary_value ?? 0; }
          if ((opp.assigned_to?.trim() ?? "") === "") row.unassigned++;
          row.total_value += opp.monetary_value ?? 0;
          const createdMs = opp.ghl_created_at ? Date.parse(opp.ghl_created_at) : NaN;
          if (!Number.isNaN(createdMs) && createdMs > 0) { const daysOpen = Math.max(0, (nowMs - createdMs) / (1000 * 60 * 60 * 24)); const current = openAgeSums.get(opp.pipeline_id) ?? { totalDays: 0, count: 0 }; current.totalDays += daysOpen; current.count += 1; openAgeSums.set(opp.pipeline_id, current); }
          continue;
        }
        if (status === "won" || status === "lost") {
          const updatedMs = opp.ghl_updated_at ? Date.parse(opp.ghl_updated_at) : NaN;
          if (!Number.isNaN(updatedMs) && updatedMs >= winRateCutoff.getTime()) { if (status === "won") row.won_last_90d++; if (status === "lost") row.lost_last_90d++; }
        }
      }
      for (const row of grouped.values()) {
        const closedCount = row.won_last_90d + row.lost_last_90d;
        row.win_rate_90d = closedCount > 0 ? Math.round((row.won_last_90d / closedCount) * 100) : null;
        const openAges = openAgeSums.get(row.pipeline_id);
        row.avg_days_open = openAges && openAges.count > 0 ? Math.round((openAges.totalDays / openAges.count) * 10) / 10 : null;
      }
      setRows(Array.from(grouped.values()).filter((r) => r.open > 0 || r.won_last_90d + r.lost_last_90d > 0).sort((a, b) => b.open - a.open));

      const stageMap = new Map<string, Map<string, { stage_name: string; stage_order: number | null; count: number }>>();
      for (const opp of opps) {
        if (String(opp.status ?? "").toLowerCase() !== "open") continue;
        const sName = opp.stage_name ?? "Unknown";
        if (!stageMap.has(opp.pipeline_id)) stageMap.set(opp.pipeline_id, new Map());
        const pStages = stageMap.get(opp.pipeline_id)!;
        if (!pStages.has(sName)) pStages.set(sName, { stage_name: sName, stage_order: opp.stage_order ?? null, count: 0 });
        pStages.get(sName)!.count++;
      }
      const stages: StageRow[] = [];
      for (const [pid, stageData] of stageMap.entries()) {
        const pName = grouped.get(pid)?.pipeline_name ?? pid;
        for (const s of stageData.values()) stages.push({ pipeline_id: pid, pipeline_name: pName, ...s });
      }
      stages.sort((a, b) => { const pc = a.pipeline_name.localeCompare(b.pipeline_name); if (pc !== 0) return pc; return (a.stage_order ?? 999) - (b.stage_order ?? 999); });
      setStageRows(stages);
    } catch (e: unknown) {
      setError((e as Error)?.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { void fetchData(); }, [fetchData]);
  return { rows, orgLocations, stageRows, hasConnections, lastSynced, loading, error, fetchData };
}

